import { useState } from "react";

import { ClientResponse } from "@/Common/Api/ClientResponse";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ButtonControl } from "../Buttons/ButtonControl";
import { TextboxControl } from "../Inputs/TextboxControl";
import { Modal } from "./Modal";

import styles from "./PasswordConfirmationPopup.module.scss";

interface IPasswordConfirmationPopupProps {
    message?: string;
    title?: string;

    clearOnError?: boolean;

    onConfirm: (password: string) => Promise<ClientResponse<any>>;
    onCancel: () => void;
}

function PasswordConfirmationPopup(props: IPasswordConfirmationPopupProps): JSX.Element {
    const { getText } = useTranslation();

    const [password, setPassword] = useState(null);
    const [error, setError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    async function confirm(password: string): Promise<void> {
        setIsProcessing(true);
        const result = await props.onConfirm(password);

        if (!result.isSuccess) {
            setError(result.errors?.[0]?.Message);

            if (props.clearOnError)
                setPassword(null);
        }
        setIsProcessing(false);
    }

    function renderFooter(): JSX.Element {
        return (
            <div className={styles.footer}>
                <ButtonControl
                    label={getText("CancelLabel")}
                    className="btn btn-default"
                    onClick={props.onCancel}
                    dataCy="passwordConfirmation-cancel"
                />
                <ButtonControl
                    label={getText("ConfirmLabel")}
                    variant="brand"
                    disabled={isProcessing || IsNullOrWhiteSpace(password)}
                    onClick={() => confirm(password)}
                    dataCy="passwordConfirmation-confirm"
                />
            </div>
        );
    }

    return (
        <Modal
            title={props.title ?? getText("ConfirmLabel")}
            width={600}
            centerContent
            fixedOverlayPosition
            showOverlay
            disableOverlayClose
            showX
            draggable
            onClose={() => props.onCancel()}
            footer={renderFooter()}
            dataCy="settings-password-confirmation-popup"
        >
            {!IsNullOrWhiteSpace(props.message) && (
                <p data-cy="passwordConfirmation-message">{props.message}</p>
            )}
            <div className={styles.passwordGrid}>
                <label
                    htmlFor="PasswordConfirmationInput"
                    className={styles.passwordLabel}
                >
                    {getText("PasswordLabel")}
                </label>
                <TextboxControl
                    value={password}
                    id="PasswordConfirmationInput"
                    type="password"
                    valid={!error}
                    onChange={password => {
                        setPassword(password);
                        setError(null);
                    }}
                    onKeyDown={e => {
                        if (e.key === "Enter" && !IsNullOrWhiteSpace(password))
                            confirm(password);
                    }}
                    onMountFocus
                    dataCy="passwordConfirmation-password"
                />
                {error && (
                    <div className={styles.passwordError} data-cy="passwordConfirmation-error">{error}</div>
                )}
            </div>
        </Modal>
    );
}

export { PasswordConfirmationPopup };
